'use client';
import Typography from '@/components/shared/typography/typography.component';
import { useTranslation } from '@/hook';
import { CarTypesEnum } from '../../../car-type';
import { formatNumber, numberWithCommas } from '@/utils';

import CarChip from '../../CarChip/CarChip';
import Image from 'next/image';
import { DiscountIcon, ChevronLeftGray, SylndrSwiftLogo } from 'public/Images';
import { DownloadApp } from '@/components/download-app';
import { useState } from 'react';
import { PriceBreakDownContent } from './PriceBreakDownContent';

interface PricingAndInstallmentSectionProps {
    sylndrSellingPrice: string | number;
    discountPrice: number;
    carType: CarTypesEnum;
    installmentPrice: number | string;
    carId: string;
}

export const PricingAndInstallmentSection = ({
    sylndrSellingPrice,
    discountPrice,
    carType,
    installmentPrice,
    carId,
}: PricingAndInstallmentSectionProps) => {
    const { t } = useTranslation();

    const hasDiscount = discountPrice !== undefined && discountPrice !== 0;

    const differencs = Math.trunc(
        parseFloat(sylndrSellingPrice as string) - discountPrice
    );

    const discountPercentage = formatNumber({ value: differencs });
    const displayedPrice = hasDiscount
        ? discountPrice.toString()
        : sylndrSellingPrice;

    // Get color based on car type
    const getCarTypeColors = () => {
        switch (carType) {
            case CarTypesEnum.LITE:
                return {
                    light: '#ffffff',
                    dark: '#0e7090', // lite-700
                };
            case CarTypesEnum.Dealer:
                return {
                    light: '#ffffff',
                    dark: '#b93815', // dealer-700
                };
            case CarTypesEnum.CERTIFIED:
                return {
                    light: '#ffffff',
                    dark: '#027a48', // certified-700
                };
            default:
                return {
                    light: '#ffffff',
                    dark: '#027a48', // blue-600
                };
        }
    };
    const getCarTypeShineStyle = () => {
        const colors = getCarTypeColors();

        return {
            background: `radial-gradient(circle at center, ${colors.light}, transparent) -200% 50% / 200% 100% no-repeat, ${colors.dark}`,
        };
    };

    const [openContent, setOpenContent] = useState(false);
    return (
        <div className="flex gap-3">
            <div
                onClick={() => setOpenContent(true)}
                className="p-3 cursor-pointer rounded-xl border border-gray-100 gap-2.5 md:w-1/2 h-full flex flex-col justify-between"
            >
                <div>
                    <div className="flex items-baseline mb-1">
                        <div dir="ltr">
                            <Typography className="font-poppins text-subtitle2SemiBold">
                                {numberWithCommas(displayedPrice as string)}
                            </Typography>
                        </div>
                        <Typography className="text-gray-800 text-caption2 ms-0.5">
                            {t('EGP')}
                        </Typography>
                        {hasDiscount && (
                            <div dir="ltr">
                                <Typography
                                    className={
                                        'text-caption line-through mx-[2px] text-gray-500 ml-2 font-poppins'
                                    }
                                >
                                    {`${numberWithCommas(sylndrSellingPrice)}`}
                                </Typography>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-1.5 mb-[10px]">
                        <CarChip>
                            <Typography className="text-gray-900 text-caption2 md:text-caption whitespace-nowrap">
                                {t('priceIsFinal')}
                            </Typography>
                        </CarChip>
                        {hasDiscount && (
                            <CarChip>
                                <div className="flex items-center gap-1.5">
                                    <Image
                                        src={DiscountIcon}
                                        alt="Sale"
                                        width={10}
                                        height={10}
                                    />
                                    <Typography className="text-gray-900 text-caption2 md:text-caption whitespace-nowrap ar:mt-0.5">
                                        {`${discountPercentage}`}
                                    </Typography>
                                </div>
                            </CarChip>
                        )}
                    </div>
                    <div className="flex gap-1.5 items-center">
                        <div className="flex-1 text-sm text-transparent line-clamp-2 leading-none">
                            <Typography
                                component="span"
                                className="animate-shine !bg-clip-text text-transparent text-caption2Medium md:text-captionMedium"
                                style={getCarTypeShineStyle()}
                            >
                                {t('carPriceDetails')}
                            </Typography>
                            <Typography
                                component="span"
                                className="animate-shine !bg-clip-text text-transparent text-caption2 md:text-caption"
                                style={{
                                    background:
                                        'radial-gradient(circle at center, #ffffff, transparent) -200% 50% / 200% 100% no-repeat, #98a2b3',
                                }}
                            >
                                {t(' ')}
                                {t('carPriceDetailsDesc')}
                            </Typography>
                        </div>
                        <Image
                            src={ChevronLeftGray}
                            width={16}
                            height={16}
                            alt="Chevron Left"
                            className="en:rotate-180"
                        />
                    </div>
                </div>
            </div>
            <div className="relative md:w-1/2 min-h-full">
                <DownloadApp
                    text=""
                    deepLink={`Financing-flow/FinancingSylndrOnboarding?carID=${carId}`}
                    source="Installment Section in Car Profile"
                    textVariant="body2SemiBold"
                    buttonProps={{
                        variant: 'swift',
                        className: 'absolute inset-0 opacity-0 cursor-pointer',
                    }}
                />
                <div className="p-3 rounded-xl border border-gray-100 gap-2.5 h-full flex flex-col justify-between pointer-events-none hover:bg-gray-50 transition-colors min-w-[160px]">
                    <div className="gap-1">
                        <Typography className="text-caption2 md:text-caption1 text-gray-800">
                            {t('carProfileFinancing')}
                        </Typography>
                        <div className="flex items-baseline">
                            <div dir="ltr">
                                <Typography className="text-gray-800 text-subtitle2SemiBold">
                                    {numberWithCommas(Number(installmentPrice))}
                                </Typography>
                            </div>
                            <Typography variant={'caption2'} className="ms-0.5">
                                {t('EGP')} {t('/month')}
                            </Typography>
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <Image
                            src={SylndrSwiftLogo}
                            width={51}
                            height={17}
                            alt="Sylndr Swift"
                        />
                        <Image
                            src={ChevronLeftGray}
                            width={16}
                            height={16}
                            alt="Chevron Left"
                            className="en:rotate-180"
                        />
                    </div>
                </div>
            </div>
            {openContent && (
                <PriceBreakDownContent
                    open={openContent}
                    setOpen={setOpenContent}
                    carType={carType}
                />
            )}
        </div>
    );
};
